import { ArrowRight } from '@dreamstack/icons/src/icon-components/ArrowRight'
import { cn } from '@dreamstack/util/src/cn'
import type { FunctionComponent, ReactElement } from 'react'
import React from 'react'
import { HeadingH3 } from './layout/Texts'

interface CommonSizeProps {
  small?: boolean
}

const TileWrapper: React.FC<
  React.PropsWithChildren<
    CommonSizeProps & { backgroundImageUrl?: string; className?: string }
  >
> = ({ small, backgroundImageUrl, className, children }) => (
  <div
    className={cn(
      'flex flex-col justify-end cursor-pointer relative bg-cover',
      small
        ? 'text-accentroGray-full'
        : 'bg-accentroGray-full text-accentroWhite-full p-px-16 h-px-196 md:p-px-24 lg:h-px-274 lg:px-px-48 lg:pb-px-32',
      className
    )}
    style={
      backgroundImageUrl
        ? {
            background: `linear-gradient(rgba(0, 0, 0, 0.1), rgba(0, 0, 0, 0.8)), url(${backgroundImageUrl})`,
          }
        : undefined
    }
  >
    {children}
  </div>
)

const SubBox: React.FC<
  React.PropsWithChildren<CommonSizeProps & { className?: string }>
> = ({ small, className, children }) => (
  <div
    className={cn(
      'flex items-center',
      small ? 'mt-px-4' : 'mt-px-12',
      className
    )}
  >
    {children}
  </div>
)

const ActionTileCaption: React.FC<
  React.PropsWithChildren<CommonSizeProps & { className?: string }>
> = ({ small, className, children }) => (
  <h4
    className={cn(
      'flex items-center space-x-px-12',
      small
        ? 'text-px-16 leading-px-24 md:text-px-20 md:leading-px-30 ml-px-16 font-normal'
        : 'text-px-16 leading-px-24 ml-px-12 font-semibold',
      className
    )}
  >
    {children}
  </h4>
)

export const StyledTileHeader: React.FC<
  React.HTMLAttributes<HTMLHeadingElement>
> = ({ className, children, ...rest }) => (
  <h4
    className={cn('text-px-20! leading-px-30 font-semibold inline', className)}
    {...rest}
  >
    {children}
  </h4>
)

const Divider: React.FC<CommonSizeProps & { className?: string }> = ({
  small,
  className,
}) => (
  <div
    className={cn(
      'h-px-4 w-px-4 rounded-full',
      small ? 'bg-accentroGray-full' : 'bg-accentroWhite-full',
      className
    )}
  />
)

const HeadingWrapper: React.FC<
  React.PropsWithChildren<CommonSizeProps & { className?: string }>
> = ({ small, className, children }) => (
  <div
    className={cn(
      'font-semibold overflow-clip break-words flex justify-between items-center',
      small
        ? 'text-px-20 leading-px-30 md:text-px-28 md:leading-px-42 lg:text-px-32 lg:leading-px-44'
        : 'text-px-20 leading-px-30 md:text-px-24 md:leading-px-36 lg:text-px-48 lg:leading-px-60',
      className
    )}
  >
    {children}
  </div>
)

export const FileDownloadInline: FunctionComponent<
  React.PropsWithChildren<FileDownloadInlineProps>
> = ({ icon, caption, fileSize, small }) => {
  return (
    <SubBox small={small}>
      {icon}
      <ActionTileCaption small={small}>
        <span className="uppercase">{caption}</span>
        {fileSize && (
          <>
            <Divider small={small} />
            <span>{fileSize}</span>
          </>
        )}
      </ActionTileCaption>
    </SubBox>
  )
}

interface FileDownloadInlineProps {
  icon?: ReactElement
  caption: string
  fileSize?: string
  small?: boolean
}

interface ActionTileProps {
  heading: string
  small?: boolean
  backgroundImgUrl?: string
  onlyLink?: boolean
}

export const ActionTile: FunctionComponent<
  React.PropsWithChildren<ActionTileProps & FileDownloadInlineProps>
> = ({ heading, small, backgroundImgUrl, onlyLink, ...rest }) => {
  return (
    <TileWrapper backgroundImageUrl={backgroundImgUrl} small={small}>
      <div>
        <HeadingWrapper>
          {small ? (
            <HeadingH3
              dangerouslySetInnerHTML={{ __html: heading }}
              title={heading}
            />
          ) : (
            <StyledTileHeader
              dangerouslySetInnerHTML={{ __html: heading }}
              title={heading}
            />
          )}
          {onlyLink && <ArrowRight className="flex-shrink-0 h-px-32 w-px-32" />}
        </HeadingWrapper>
        {!onlyLink && <FileDownloadInline {...rest} small={small} />}
      </div>
    </TileWrapper>
  )
}
